import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import axios from 'axios';
import {DataGrid, GridToolbar} from '@mui/x-data-grid';
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {baseUrl} from '../Univ.js';
const EmrDetails = (props) => {
    const {pat_id} = useParams();
    const [rows, setRows] = useState([]);
    const [rows1, setRows1] = useState([]);
    const [patientData, setPatientData] = useState([]);
    const [pharmacyRows, setPharmacyRows] = useState([]);
    const [viewPharmData, setViewPharmData] = useState([]);
    const [trendData, setTrendData] = useState([]);
    // =========================================================
    // FETCH EMR DATA
    // =========================================================
    useEffect(() => {
        const fetchData = async () => {
            try {
                // =================================================
                // PATIENT
                // =================================================
                const response1 = await axios.get(
                    `${baseUrl}pat-id/${pat_id}/`
                );
                if (
                    response1.status !== 200 &&
                    response1.status !== 201
                ) {
                    window.location.href = '/pat-search/';
                    return;
                }
                setPatientData(response1.data);
                if (
                    !response1.data ||
                    response1.data.length === 0
                ) {
                    window.location.href = '/pat-search/';
                    return;
                }
                const patId = response1.data[0].id;
                // =================================================
                // LAB
                // =================================================
                const response2 = await axios.get(
                    `${baseUrl}emr-lab-op/${patId}/`
                );
                const labData = Array.isArray(response2.data)
                    ? response2.data.map((data) => ({
                        ...data,
                        type: 'lab',
                    }))
                    : [];
                // =================================================
                // INVESTIGATION
                // =================================================
                const response3 = await axios.get(
                    `${baseUrl}emr-inv-op/${patId}/`
                );
                const invData = Array.isArray(response3.data)
                    ? response3.data.map((data) => ({
                        ...data,
                        type: 'inv',
                    }))
                    : [];
                // =================================================
                // OP BILLING
                // =================================================
                const response4 = await axios.get(
                    `${baseUrl}emr-op/${patId}/`
                );
                const opData = Array.isArray(response4.data)
                    ? response4.data.map((data) => ({
                        ...data,
                        type: 'op',
                    }))
                    : [];
                // DEBUG
                console.log(
                    '================ EMR OP ================'
                );
                console.log(
                    'EMR OP RAW:',
                    response4.data
                );
                console.log(
                    'EMR OP DATA:',
                    opData
                );
                console.log(
                    '========================================='
                );
                // =================================================
                // OP CASE / CONSULTATION
                // =================================================
                const caseData = [];
                for (const opItem of opData) {
                    const apptId = opItem.appt_id;
                    if (
                        apptId !== undefined &&
                        apptId !== null &&
                        apptId !== ''
                    ) {
                        try {
                            const response5 = await axios.get(
                                `${baseUrl}view-appt/${apptId}/`
                            );
                            if (
                                Array.isArray(response5.data)
                            ) {
                                const caseData1 =
                                    response5.data.map((data) => ({
                                        ...data,
                                        type: 'case',
                                    }));
                                caseData.push(...caseData1);
                            }
                        } catch (caseError) {
                            console.log(
                                `Unable to fetch case for appointment ${apptId}`,
                                caseError
                            );
                        }
                    }
                }
                // =================================================
                // COMBINE OP EMR
                // =================================================
                //
                // IMPORTANT:
                // Do NOT filter opData using lab/investigation IDs.
                //
                // The previous code was removing OP records because
                // uniqueIds did not contain OP appointment IDs.
                //
                // =================================================
                const combined = [
                    ...labData,
                    ...invData,
                    ...opData,
                    ...caseData,
                ];
                console.log(
                    'FINAL COMBINED EMR ROWS:',
                    combined
                );
                setRows(combined);
                // =================================================
                // IP STATUS
                // =================================================
                const response6 = await axios.get(
                    `${baseUrl}fetch-ip-status/${patId}`
                );
                const ipData = Array.isArray(response6.data)
                    ? response6.data.map((data) => ({
                        id: data.id,
                        ip_id: data.ip_id,
                        pat__pat_id:
                            data.pat?.pat_id || '',
                        pat__patient:
                            data.pat?.patient || '',
                        pat__age:
                            data.pat?.age || '',
                        pat__mobile:
                            data.pat?.mobile || '',
                        pat__address1:
                            data.pat?.address1 || '',
                        room_no__room_no:
                            data.room_no?.room_no || '',
                        payment:
                            data.payment || '',
                        admit_date:
                            data.admit_date || '',
                        discharge_time:
                            data.discharge_time || '',
                        action: '',
                        summary: '',
                    }))
                    : [];
                setRows1(ipData);
                // =================================================
                // PHARMACY
                // =================================================
                const response7 = await axios.get(
                    `${baseUrl}emr-pharmacy-sale/${patId}/`
                );
                const pharmacyData =
                    Array.isArray(response7.data)
                        ? response7.data.map((data, index) => ({
                            id: data.id || index,
                            sale_id:
                                data.sale_id || '',
                            product:
                                data.product || '',
                            qty:
                                data.qty || '',
                            paid:
                                data.paid || '',
                            add_date:
                                data.add_date || '',
                            addedby:
                                data.addedby || '',
                        }))
                        : [];
                setPharmacyRows(pharmacyData);
                // =================================================
                // PATIENT TREND
                // =================================================
                const response8 = await axios.get(
                    `${baseUrl}emr-patient-trend-data/${patId}/`
                );
                const trends =
                    Array.isArray(response8.data)
                        ? response8.data.map((item) => ({
                            ...item,
                            sugar:
                                Number(item.sugar || 0),
                            bp:
                                Number(item.bp || 0),
                            weight:
                                Number(item.weight || 0),
                            tempr:
                                Number(item.tempr || 0),
                            pulse:
                                Number(item.pulse || 0),
                            spo2:
                                Number(item.spo2 || 0),
                            bmi:
                                Number(item.bmi || 0),
                        }))
                        : [];
                setTrendData(trends);
            } catch (error) {
                console.error(
                    'EMR Details Error:',
                    error
                );
            }
        };
        fetchData();
    }, [pat_id]);
    // =========================================================
    // PHARMACY PRODUCT DETAILS
    // =========================================================
    useEffect(() => {
        if (
            !pharmacyRows ||
            pharmacyRows.length === 0
        ) {
            return;
        }
        const ids = [
            ...new Set(
                pharmacyRows.flatMap((row) =>
                    row.product
                        ? row.product
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                        : []
                )
            )
        ];
        if (ids.length === 0) {
            setViewPharmData([]);
            return;
        }
        axios
            .post(
                `${baseUrl}bulk-indent-use-bill/`,
                {
                    ids: ids,
                }
            )
            .then((res) => {
                setViewPharmData(
                    Array.isArray(res.data)
                        ? res.data
                        : []
                );
            })
            .catch((error) => {
                console.error(
                    'Pharmacy product error:',
                    error
                );
            });
    }, [pharmacyRows]);
    // =========================================================
    // PHARMACY COLUMNS
    // =========================================================
    const pharmacyColumns = [
        {
            field: 'sale_id',
            headerName: 'Sale ID',
            width: 140,
        },
        {
            field: 'product',
            headerName: 'Medicine',
            width: 400,
            renderCell: (params) => {
                const productIds =
                    params.row.product
                        ? params.row.product
                            .split(',')
                            .map((item) => item.trim())
                        : [];
                const qtys =
                    params.row.qty
                        ? params.row.qty
                            .split(',')
                            .map((item) => item.trim())
                        : [];
                return (
                    <div
                        style={{
                            lineHeight: '1.5',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        }}
                    >

                        {productIds.map(
                            (id, index) => {
                                const medicine =
                                    viewPharmData.find(
                                        (item) =>
                                            String(item.id) ===
                                            String(id)
                                    );
                                return (
                                    <div key={index}>

                                        {medicine?.product_name ||
                                            'Unknown Medicine'}

                                        {qtys[index]
                                            ? ` - Qty: ${qtys[index]}`
                                            : ''}

                                    </div>
                                );
                            }
                        )}

                    </div>
                );
            },
        },
        {
            field: 'add_date',
            headerName: 'Date',
            width: 140,
        },
        {
            field: 'addedby',
            headerName: 'Added By',
            width: 130,
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Link
                    to={`/pharm-invoice-other/${params.row.sale_id}/`}
                    className="btn btn-primary"
                >
                    View
                </Link>
            ),
        },
        {
            field: 'refill',
            headerName: 'Refill',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Link
                    to={`/drug-refill/${params.row.id}`}
                    className="btn btn-success"
                >
                    Refill
                </Link>
            ),
        },
    ];
    // =========================================================
    // OP EMR COLUMNS
    // =========================================================
    const columns = [
        {
            field: 'add_date',
            headerName: 'Appt Date',
            width: 120,
        },
        {
            field: 'appt_id',
            headerName: 'Appt ID',
            width: 100,
        },
        {
            field: 'test_name',
            headerName: 'Details',
            width: 400,
            renderCell: (params) => {
                const d = params.row;
                if (!d) {
                    return '';
                }
                if (d.type === 'lab') {
                    return (
                        d.lab_test ||
                        'Laboratory'
                    );
                }
                if (d.type === 'inv') {
                    return (
                        d.inv_test ||
                        'Investigation'
                    );
                }
                if (d.type === 'op') {
                    return (
                        `OP Consultation${
                            d.invoice
                                ? ` - ${d.invoice}`
                                : ''
                        }`
                    );
                }
                if (d.type === 'case') {
                    return 'Consultation / Case Sheet';
                }
                return '';
            },
        },
        {
            field: 'addedby',
            headerName: 'Added By',
            width: 120,
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                // LAB
                if (
                    row.type === 'lab'
                ) {
                    return (
                        <Link
                            to={`/lab-report-all/${row.appt_id}/`}
                            className="btn btn-success"
                        >
                            Lab
                        </Link>
                    );
                }
                // INVESTIGATION
                if (
                    row.type === 'inv'
                ) {
                    return (
                        <Link
                            to={`/inv-report-all/${row.appt_id}/`}
                            className="btn btn-success"
                        >
                            Inv
                        </Link>
                    );
                }
                // CASE SHEET
                if (
                    row.type === 'case'
                ) {
                    return (
                        <Link
                            to={`/op-case/${row.id}/`}
                            className="btn btn-success"
                        >
                            OP
                        </Link>
                    );
                }
                // OP BILL
                if (
                    row.type === 'op'
                ) {
                    return (
                        <Link
                            to={`/op-case/${row.appt_id}/`}
                            className="btn btn-primary"
                        >
                            View
                        </Link>
                    );
                }
                return null;
            },
        },
    ];
    // =========================================================
    // IP COLUMNS
    // =========================================================
    const columns1 = [
        {
            field: 'ip_id',
            headerName: 'IP ID',
            width: 100,
            renderCell: (params) => (
                <Link
                    to={`/ip-pat-view/${params.row.id}/`}
                >
                    {params.value}
                </Link>
            ),
        },
        {
            field: 'pat__pat_id',
            headerName: 'Pat ID',
            width: 100,
        },
        {
            field: 'pat__patient',
            headerName: 'Patient',
            width: 180,
        },
        {
            field: 'pat__age',
            headerName: 'Age',
            width: 70,
        },
        {
            field: 'pat__mobile',
            headerName: 'Mobile',
            width: 130,
        },
        {
            field: 'pat__address1',
            headerName: 'Address',
            width: 220,
        },
        {
            field: 'room_no__room_no',
            headerName: 'Room No',
            width: 100,
        },
        {
            field: 'payment',
            headerName: 'Payment',
            width: 100,
        },
        {
            field: 'admit_date',
            headerName: 'Admitted On',
            width: 120,
        },
        {
            field: 'discharge_time',
            headerName: 'Discharged On',
            width: 140,
        },
        {
            field: 'summary',
            headerName: 'Summary',
            width: 110,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Link
                    to={`/discharge-summary/${params.row.id}/`}
                    className="btn btn-warning"
                >
                    Summary
                </Link>
            ),
        },
    ];
    // =========================================================
    // RENDER
    // =========================================================
    return (
        <section className="container mt-4">


            {/* =================================================
                PATIENT HEADER
            ================================================= */}

            <div className="row table-responsive">

                <div className="col-md-12 col-12 mb-2">

                    <table className="erp-table-modern">

                        <thead>

                        <tr
                            style={{
                                backgroundColor: '#1bc5ce',
                                color: 'white'
                            }}
                        >

                            <th>Patient ID</th>
                            <th>Patient Name</th>
                            <th>Gender</th>
                            <th>D.O.B/Age</th>
                            <th>Mobile</th>
                            <th>Regd. On</th>

                        </tr>

                        </thead>


                        <tbody>

                        {patientData.map(
                            (patient, index) => (
                                <tr key={index}>

                                    <td>
                                        {patient.pat_id}
                                    </td>

                                    <td>
                                        {patient.patient}
                                    </td>

                                    <td>
                                        {patient.gender}
                                    </td>

                                    <td>
                                        {patient.age}
                                    </td>

                                    <td>
                                        {patient.mobile}
                                    </td>

                                    <td>
                                        {patient.add_date}
                                    </td>

                                </tr>
                            )
                        )}

                        </tbody>

                    </table>

                </div>

            </div>


            {/* =================================================
                OP EMR
            ================================================= */}

            <div
                style={{
                    height: '100%',
                    width: '100%'
                }}
            >

                <h3
                    className="text-center"
                    style={{
                        color: 'white',
                        backgroundColor: '#0dcaf0',
                        padding: '10px'
                    }}
                >

                    <i
                        className="fa-solid fa-calendar-days"
                        aria-hidden="true"
                        style={{
                            color: 'red',
                            marginRight: '8px'
                        }}
                    />

                    OP EMR Details

                </h3>


                <DataGrid

                    className="mt-4"

                    sx={{
                        boxShadow: 4,
                        border: 4,
                        borderColor: 'primary.light',
                        '& .MuiDataGrid-cell:hover': {
                            color: 'primary.main',
                        },
                    }}

                    getRowId={(row) =>
                        `${row.type}-${row.id}`
                    }

                    rows={rows}

                    columns={columns}

                    pageSize={10}

                    slots={{
                        toolbar: GridToolbar
                    }}

                    autoHeight

                />

            </div>


            {/* =================================================
                PHARMACY
            ================================================= */}

            <div
                style={{
                    height: '100%',
                    width: '100%'
                }}
                className="mt-5"
            >

                <h3
                    className="text-center"
                    style={{
                        color: 'white',
                        backgroundColor: '#198754',
                        padding: '10px'
                    }}
                >

                    <i
                        className="fa-solid fa-pills"
                        aria-hidden="true"
                        style={{
                            color: 'white',
                            marginRight: '8px'
                        }}
                    />

                    Pharmacy Sale Details

                </h3>


                <DataGrid

                    className="mt-4"

                    sx={{
                        boxShadow: 4,
                        border: 4,
                        borderColor: 'success.light',
                        '& .MuiDataGrid-cell:hover': {
                            color: 'success.main',
                        },
                    }}

                    rows={pharmacyRows}

                    columns={pharmacyColumns}

                    pageSize={10}

                    slots={{
                        toolbar: GridToolbar
                    }}

                    autoHeight

                    getRowHeight={() => 'auto'}

                />

            </div>


            {/* =================================================
                IP EMR
            ================================================= */}

            <div
                style={{
                    height: '100%',
                    width: '100%'
                }}
                className="mt-5"
            >

                <h3
                    className="text-center"
                    style={{
                        color: 'white',
                        backgroundColor: '#0dcaf0',
                        padding: '10px'
                    }}
                >

                    <i
                        className="fa-solid fa-calendar-days"
                        aria-hidden="true"
                        style={{
                            color: 'red',
                            marginRight: '8px'
                        }}
                    />

                    IP EMR Details

                </h3>


                <DataGrid

                    className="mt-4"

                    sx={{
                        boxShadow: 4,
                        border: 4,
                        borderColor: 'primary.light',
                        '& .MuiDataGrid-cell:hover': {
                            color: 'primary.main',
                        },
                    }}

                    rows={rows1}

                    columns={columns1}

                    pageSize={10}

                    slots={{
                        toolbar: GridToolbar
                    }}

                    autoHeight

                    getRowId={(row) =>
                        row.id
                    }

                />

            </div>


            {/* =================================================
                PATIENT TREND
            ================================================= */}

            <div
                style={{
                    height: '100%',
                    width: '100%'
                }}
                className="mt-5"
            >

                <h3
                    className="text-center"
                    style={{
                        color: 'white',
                        backgroundColor: '#6610f2',
                        padding: '10px'
                    }}
                >

                    <i
                        className="fa-solid fa-chart-line"
                        aria-hidden="true"
                        style={{
                            color: 'yellow',
                            marginRight: '8px'
                        }}
                    />

                    Patient Trend Analysis

                </h3>


                <div className="row mt-4">


                    {/* =================================================
                        SUGAR
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-danger text-white">
                                Sugar Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="sugar"
                                            stroke="#dc3545"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        BP
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-primary text-white">
                                BP Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="bp"
                                            stroke="#0d6efd"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        WEIGHT
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-success text-white">
                                Weight Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="weight"
                                            stroke="#198754"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        TEMPERATURE
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-warning text-dark">
                                Temperature Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="tempr"
                                            stroke="#ffc107"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        PULSE
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-info text-white">
                                Pulse Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="pulse"
                                            stroke="#0dcaf0"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        SPO2
                    ================================================= */}

                    <div className="col-md-6 mb-4">

                        <div className="card shadow">

                            <div className="card-header bg-secondary text-white">
                                SPO2 Trend
                            </div>

                            <div className="card-body">

                                <ResponsiveContainer
                                    width="100%"
                                    height={300}
                                >

                                    <LineChart
                                        data={trendData}
                                    >

                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                        />

                                        <XAxis
                                            dataKey="add_date"
                                        />

                                        <YAxis/>

                                        <Tooltip/>

                                        <Legend/>

                                        <Line
                                            type="monotone"
                                            dataKey="spo2"
                                            stroke="#6c757d"
                                            strokeWidth={3}
                                            dot={{r: 5}}
                                            activeDot={{r: 8}}
                                        />

                                    </LineChart>

                                </ResponsiveContainer>

                            </div>

                        </div>

                    </div>

                </div>

            </div>

        </section>
    );
};
export default EmrDetails;
